// ── Hydraulic Calculations ─────────────────────────────────────────────
// Hazen-Williams friction loss, pipe velocity, and pipe sizing utilities.
// Extracted from logic.js for modularity (issue #139) and typed (issue #140).

export interface PipeMaterial {
  c: number;
}

/** Pipe materials and their Hazen-Williams C coefficients. */
export const PIPE_MATERIALS: Record<string, PipeMaterial> = {
  PE:     { c: 150 },
  PVC:    { c: 140 },
  copper: { c: 130 },
  steel:  { c: 120 },
};

/** Standard pipe diameters in mm. */
export const STANDARD_PIPE_DIAMETERS: number[] = [16, 20, 25, 32, 40, 50, 63, 75, 90, 110];

/**
 * Metres of water column per bar (at 4 degC).
 *
 * `calculateFrictionLoss` returns METRES of head, because that is what the
 * SI form of Hazen-Williams yields. Everything the planner shows the user is
 * in bar, so head must be divided by this before it meets a supply pressure.
 */
export const METRES_OF_HEAD_PER_BAR = 10.197;

/**
 * Hazen-Williams friction loss (meters of head per length).
 *
 * Formula: hf = (10.67 * Q^1.852) / (C^1.852 * D^4.87) * L
 *
 * @param flow      — flow rate in l/min
 * @param diameter  — inner diameter in mm
 * @param length    — pipe length in meters
 * @param material  — pipe material key (default "PE")
 */
export function calculateFrictionLoss({
  flow,
  diameter,
  length,
  material = 'PE',
}: {
  flow: number;
  diameter: number;
  length: number;
  material?: string;
}): number {
  if (flow <= 0 || diameter <= 0 || length <= 0) return 0;
  const c = (PIPE_MATERIALS[material] || PIPE_MATERIALS.PE).c;
  const qM3s = flow / 60000;         // l/min → m³/s
  const dM = diameter / 1000;         // mm → m
  return (10.67 * Math.pow(qM3s, 1.852)) / (Math.pow(c, 1.852) * Math.pow(dM, 4.87)) * length;
}

/**
 * Velocity in pipe (m/s).
 *
 * @param flow     — flow rate in l/min
 * @param diameter — inner diameter in mm
 */
export function calculatePipeVelocity({
  flow,
  diameter,
}: {
  flow: number;
  diameter: number;
}): number {
  if (flow <= 0 || diameter <= 0) return 0;
  const qM3s = flow / 60000;         // l/min → m³/s
  const rM = (diameter / 1000) / 2;  // mm → m radius
  const area = Math.PI * rM * rM;
  return qM3s / area;
}

/**
 * Recommend minimum pipe diameter for a given flow rate,
 * ensuring velocity stays below maxVelocity (default 1.5 m/s).
 */
export function recommendPipeDiameter({
  flow,
  maxVelocity = 1.5,
}: {
  flow: number;
  maxVelocity?: number;
}): number {
  if (flow <= 0) return STANDARD_PIPE_DIAMETERS[0];
  for (const d of STANDARD_PIPE_DIAMETERS) {
    const v = calculatePipeVelocity({ flow, diameter: d });
    if (v <= maxVelocity) return d;
  }
  return STANDARD_PIPE_DIAMETERS[STANDARD_PIPE_DIAMETERS.length - 1];
}
